import { useEffect, useState } from 'react';
import { ExternalLinkIcon, RefreshCwIcon } from 'lucide-react';
import { initialInventory, INVENTORY_CSV_URL, INVENTORY_SHEET_URL, InventoryRecord, InventorySnapshot } from '../data/inventory';
const CACHE_KEY = 'pegasus-inventory-v1';
const FRESH_FOR = 10 * 60 * 1000;
let pending: Promise<InventorySnapshot> | null = null;
function readCache(): InventorySnapshot {
  try {
    const value = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (value && Number.isFinite(Date.parse(value.retrievedAt)) && Array.isArray(value.records) && value.records.every((row: InventoryRecord) => Number.isFinite(row.sourceRow) && ['model', 'sizeSeats', 'manufacturer', 'color', 'cabinet', 'smartTub', 'location', 'available', 'notes'].every(key => typeof row[key as keyof InventoryRecord] === 'string')) && Date.parse(value.retrievedAt) > Date.parse(initialInventory.retrievedAt)) return value;
  } catch {/* Storage can be unavailable in private browsing. */}
  return initialInventory;
}

/** Published CSV may contain quoted commas and newlines inside cells. */
export function parseInventoryCsv(csv: string): InventoryRecord[] {
  const rows: string[][] = [];
  let row: string[] = [],
    cell = '',
    quoted = false;
  for (let i = 0; i < csv.length; i++) {
    const char = csv[i];
    if (char === '"') {
      if (quoted && csv[i + 1] === '"') {
        cell += '"';
        i++;
      } else quoted = !quoted;
    } else if (char === ',' && !quoted) {
      row.push(cell);
      cell = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && csv[i + 1] === '\n') i++;
      row.push(cell);
      if (row.some(value => value.trim())) rows.push(row);
      row = [];
      cell = '';
    } else cell += char;
  }
  if (quoted) throw new Error('Incomplete inventory response');
  row.push(cell);
  if (row.some(value => value.trim())) rows.push(row);
  const headers = (rows.shift() || []).map(value => value.replace(/^\uFEFF/, '').trim());
  const expected = ['Model', 'Size · Seats', 'Manufacturer', 'Color', 'Cabinet', 'SmartTub', 'Location', 'Available', 'Notes'];
  if (headers.length !== expected.length || !expected.every((name, index) => headers[index] === name)) throw new Error('Inventory format changed');
  if (rows.some(cells => cells[0]?.trim() && cells.length !== expected.length)) throw new Error('Incomplete inventory row');
  return rows.filter(cells => cells[0]?.trim()).map((cells, index) => ({
    sourceRow: index + 2,
    model: cells[0]?.trim() || '',
    sizeSeats: cells[1]?.trim() || '',
    manufacturer: cells[2]?.trim() || '',
    color: cells[3]?.trim() || '',
    cabinet: cells[4]?.trim() || '',
    smartTub: cells[5]?.trim() || '',
    location: cells[6]?.trim() || '',
    available: cells[7]?.trim() || '',
    notes: cells[8]?.trim() || ''
  }));
}
function refreshInventory(force = false): Promise<InventorySnapshot> {
  const cached = readCache();
  if (!force && Date.now() - Date.parse(cached.retrievedAt) < FRESH_FOR) return Promise.resolve(cached);
  if (pending) return pending;
  pending = (async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);
    try {
      const response = await fetch(INVENTORY_CSV_URL, {
        signal: controller.signal,
        credentials: 'omit',
        cache: 'no-cache'
      });
      if (!response.ok) throw new Error('Inventory refresh unavailable');
      const value = {
        retrievedAt: new Date().toISOString(),
        records: parseInventoryCsv(await response.text())
      };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
      } catch {/* Public inventory remains available without storage. */}
      return value;
    } finally {
      clearTimeout(timeout);
      pending = null;
    }
  })();
  return pending;
}
export function AvailableInventory() {
  const [snapshot, setSnapshot] = useState<InventorySnapshot>(readCache);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [brand, setBrand] = useState('all');
  const [location, setLocation] = useState('all');
  const [showAll, setShowAll] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setRefreshing(true);
    refreshInventory().then(value => {
      if (!cancelled) {
        setSnapshot(value);
        setRefreshFailed(false);
      }
    }).catch(() => {
      if (!cancelled) setRefreshFailed(true);
    }).finally(() => {
      if (!cancelled) setRefreshing(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  async function refresh() {
    setRefreshing(true);
    setRefreshFailed(false);
    try {
      setSnapshot(await refreshInventory(true));
    } catch {
      setRefreshFailed(true);
    } finally {
      setRefreshing(false);
    }
  }
  const brands = [...new Set(snapshot.records.map(item => item.manufacturer))].sort();
  const locations = [...new Set(snapshot.records.map(item => item.location))].sort();
  const term = query.trim().toLowerCase();
  const filtered = snapshot.records.filter(item => (brand === 'all' || item.manufacturer === brand) && (location === 'all' || item.location === location) && (!term || Object.values(item).join(' ').toLowerCase().includes(term)));
  const visible = showAll ? filtered : filtered.slice(0, 6);
  const checked = new Date(snapshot.retrievedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Detroit',
    timeZoneName: 'short'
  });
  return <div id="inventory-list" className="scroll-mt-28" data-id="element-88" data-mp="38wpxz3vjp1q0">
      <div className="grid grid-cols-1 gap-4 rounded-card bg-water p-5 sm:grid-cols-2 lg:grid-cols-3 sm:p-6" data-id="element-89" data-mp="38yj5eldzfpz2">
        <label className="text-[15px] font-bold text-ink" data-id="element-90" data-mp="34u346xfbd6q6">Find a model or color
          <input value={query} onChange={event => {
          setQuery(event.target.value);
          setShowAll(false);
        }} placeholder="Search availability" className="mt-2 min-h-[48px] w-full rounded-btn border border-ink/25 bg-white px-3 text-[16px] font-normal" data-id="element-91" data-mp="2zwtfjjzr0z3e" />
        </label>
        <label className="text-[15px] font-bold text-ink" data-id="element-92" data-mp="t5bwhbkci8ka">Brand
          <select value={brand} onChange={event => {
          setBrand(event.target.value);
          setShowAll(false);
        }} className="mt-2 min-h-[48px] w-full rounded-btn border border-ink/25 bg-white px-3 text-[16px] font-normal" data-id="element-93" data-mp="t40rj8f5yurl">
            <option value="all" data-id="element-94" data-mp="2dnw1cfjurjlv">All brands</option>{brands.map(value => <option key={value} value={value} data-id="element-95" data-mp="2dppca71i0pzo">{value === 'SD' ? 'Sundance' : value}</option>)}
          </select>
        </label>
        <label className="text-[15px] font-bold text-ink" data-id="element-96" data-mp="2ikmmhhail5e5">Location
          <select value={location} onChange={event => {
          setLocation(event.target.value);
          setShowAll(false);
        }} className="mt-2 min-h-[48px] w-full rounded-btn border border-ink/25 bg-white px-3 text-[16px] font-normal" data-id="element-97" data-mp="36d34jru94xdg">
            <option value="all" data-id="element-98" data-mp="2wgq8wof5c9g2">All locations</option>{locations.map(value => <option key={value} value={value} data-id="element-99" data-mp="31c5obkssydel">{value}</option>)}
          </select>
        </label>
      </div>
      <div className="mb-6 mt-4 flex flex-col gap-2 text-[14px] leading-6 text-ink/70 sm:flex-row sm:items-center sm:justify-between" data-id="element-100" data-mp="1kxnliucshbag">
        <p data-id="element-101" data-mp="80olfgu21e8g">Last checked {checked}. Confirm availability and timing before visiting.</p>
        <button type="button" disabled={refreshing} onClick={refresh} className="inline-flex min-h-[44px] shrink-0 items-center gap-2 font-bold text-pegasus-blue underline underline-offset-4 disabled:cursor-wait disabled:opacity-65" data-id="element-102" data-mp="82hvl63vmgl7"><RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-103" data-mp="d01c6co1r5gb" />{refreshing ? 'Checking for updates…' : 'Refresh availability'}</button>
      </div>
      {refreshFailed && <p role="status" className="mb-5 rounded-btn bg-water p-4 text-[15px] leading-6 text-ink/80" data-id="element-104" data-mp="3ldgyekviv8qv">Showing the last checked list. Please confirm current availability with us before visiting.</p>}
      <p role="status" className="mb-5 text-[15px] text-ink/70" data-id="element-105" data-mp="3u9zntn4joeip">Showing {visible.length} of {filtered.length} {filtered.length === 1 ? 'listing' : 'listings'}.</p>
      {filtered.length ? <ul className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-106" data-mp="31prdmxm068r5">
          {visible.map(item => <li key={`${item.sourceRow}-${item.model}`} className="rounded-card border border-ink/15 bg-white p-5 sm:p-6" data-id="element-107" data-mp="2xo649m8ogclp">
              <p className="text-[13px] font-bold uppercase tracking-wide text-pine" data-id="element-108" data-mp="3rjl0dgp8sg61">{item.manufacturer === 'SD' ? 'Sundance' : item.manufacturer}</p>
              <h3 className="mt-2 font-display text-[24px] font-semibold leading-8 text-ink" data-id="element-109" data-mp="3vjwmkxx96ojm">{item.model}</h3>
              <p className="mt-3 whitespace-pre-line text-[16px] leading-6 text-ink/80" data-id="element-110" data-mp="4ggbf8u100t3">{item.sizeSeats}</p>
              <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 border-t border-ink/15 pt-4 text-[15px] leading-6" data-id="element-111" data-mp="9c5cjglp1pri">
                <dt className="font-semibold text-ink" data-id="element-112" data-mp="j4b62urlgf8t">Color</dt><dd className="text-ink/75" data-id="element-113" data-mp="j4ku4ct2wcbv">{item.color || '—'}</dd>
                <dt className="font-semibold text-ink" data-id="element-114" data-mp="n5wdi0ld01bm">Cabinet</dt><dd className="text-ink/75" data-id="element-115" data-mp="n7qwh9d2ljd2">{item.cabinet || '—'}</dd>
                <dt className="font-semibold text-ink" data-id="element-116" data-mp="s625xizgdrur">SmartTub</dt><dd className="text-ink/75" data-id="element-117" data-mp="x3u24p74s04r">{item.smartTub || '—'}</dd>
                <dt className="font-semibold text-ink" data-id="element-118" data-mp="x1pxg51eqx3f">Location</dt><dd className="text-ink/75" data-id="element-119" data-mp="1780xubm8y4as">{item.location || '—'}</dd>
                <dt className="font-semibold text-ink" data-id="element-120" data-mp="128o211dedmw2">Available</dt><dd className="text-ink/75" data-id="element-121" data-mp="127wi4nt5xdlm">{item.available || 'Confirm with us'}</dd>
              </dl>
              {item.notes && <p className="mt-4 border-t border-ink/15 pt-4 text-[15px] leading-6 text-ink/75" data-id="element-122" data-mp="x9uxhyg39681">{item.notes}</p>}
            </li>)}
        </ul> : <p className="rounded-card border border-ink/15 p-6 text-[17px] leading-7 text-ink/80" data-id="element-123" data-mp="1pyibra2f6vc3">No listings match these filters. Try another brand, location or search.</p>}
      <div className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-2" data-id="element-124" data-mp="1g8fm9juibzii">
        {!showAll && filtered.length > 6 && <button type="button" onClick={() => setShowAll(true)} className="min-h-[48px] rounded-btn border border-pegasus-blue px-5 text-[16px] font-bold text-pegasus-blue hover:bg-water" data-id="element-125" data-mp="1c73r6ysv6m2x">Show all {filtered.length} listings</button>}
        <button type="button" onClick={() => {
        setQuery('');
        setBrand('all');
        setLocation('all');
        setShowAll(false);
      }} className="min-h-[44px] text-[15px] font-bold text-pegasus-blue underline underline-offset-4" data-id="element-126" data-mp="23wys7sq3407f">Clear filters</button>
        <a href={INVENTORY_SHEET_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-2 text-[15px] font-bold text-pegasus-blue underline underline-offset-4" data-id="element-127" data-mp="2g746a9fiki0h">View the original list<ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-128" data-mp="34305rtgedv4d" /></a>
      </div>
    </div>;
}